import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + '/api';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [needsDonationSetup, setNeedsDonationSetup] = useState(false);

  useEffect(() => {
    async function initAuth() {
      if (token) {
        try {
          const response = await axios.get(API + '/auth/me', {
            headers: { Authorization: 'Bearer ' + token }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    }
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(API + '/auth/login', { email, password });
    const data = response.data;
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    setNeedsDonationSetup(data.needs_donation_setup);
    return { user: data.user, needsDonationSetup: data.needs_donation_setup };
  };

  const signup = async (email, password, username) => {
    const response = await axios.post(API + '/auth/signup', { email, password, username });
    const data = response.data;
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setNeedsDonationSetup(false);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(API + '/auth/profile', data, {
      headers: { Authorization: 'Bearer ' + token }
    });
    setUser(response.data);
    return response.data;
  };

  const refreshUser = async () => {
    if (token) {
      const response = await axios.get(API + '/auth/me', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setUser(response.data);
    }
  };

  const value = {
    user,
    token,
    loading,
    needsDonationSetup,
    login,
    signup,
    logout,
    updateProfile,
    refreshUser,
    setUser,  // Exposed for Google OAuth
    setToken, // Exposed for Google OAuth
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isCreator: user?.subscription_tier === 'creator' || user?.subscription_tier === 'contributor' || user?.role === 'admin' || user?.role === 'super_admin',
    isContributor: user?.subscription_tier === 'contributor' || user?.role === 'admin' || user?.role === 'super_admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
